<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { ChevronRight, User, BookOpen } from 'lucide-vue-next'

interface SubjectTutor {
  id: number
  name: string
  avatar_url: string | null
}

interface Subject {
  id: number
  name: string
  slug: string
  color: string
  icon: string
  tutor_count: number
  tutors: SubjectTutor[]
}

defineProps<{
  subjects: Subject[]
}>()

function getSubjectGradient(color: string): string {
  const gradients: Record<string, string> = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
    teal: 'from-teal-500 to-teal-600',
    pink: 'from-pink-500 to-pink-600',
    indigo: 'from-indigo-500 to-indigo-600',
  }
  return gradients[color] || gradients.blue
}
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-gray-900">Explore by Subject</h3>
      <Link href="/student/explore" class="text-blue-600 text-sm font-medium hover:underline flex items-center">
        View All
        <ChevronRight class="w-4 h-4 ml-1" />
      </Link>
    </div>

    <div v-if="subjects.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Link
        v-for="subject in subjects"
        :key="subject.id"
        :href="`/student/explore`"
        class="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
      >
        <!-- Subject Icon -->
        <div
          :class="[
            'w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br text-white',
            getSubjectGradient(subject.color)
          ]"
        >
          <span class="text-xl">{{ subject.icon }}</span>
        </div>

        <!-- Subject Name -->
        <h4 class="font-semibold text-gray-900 mb-1">{{ subject.name }}</h4>
        <p class="text-sm text-gray-500 mb-3">{{ subject.tutor_count }} tutors</p>

        <!-- Tutor Avatars -->
        <div class="flex items-center -space-x-2">
          <div
            v-for="tutor in subject.tutors.slice(0, 4)"
            :key="tutor.id"
            class="w-8 h-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center overflow-hidden"
          >
            <img
              v-if="tutor.avatar_url"
              :src="tutor.avatar_url"
              :alt="tutor.name"
              class="w-full h-full object-cover"
            />
            <User v-else class="w-4 h-4 text-gray-400" />
          </div>
          <div
            v-if="subject.tutor_count > 4"
            class="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600"
          >
            +{{ subject.tutor_count - 4 }}
          </div>
        </div>
      </Link>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400">
      <BookOpen class="w-12 h-12 mx-auto mb-3 opacity-50" />
      <p>No subjects available</p>
    </div>
  </div>
</template>
